import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../lib/api';
import { AddressAutocomplete } from '../components/AddressAutocomplete';

// Derive a readable display name from the camelCase home id ("10SelbyDr" → "10 Selby Dr").
function deriveDisplayName(homeId: string): string {
  if (!homeId) return '';
  return homeId
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/([0-9])([A-Z])/g, '$1 $2');
}

export function CreateHomePage() {
  const api = useApi();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    homeId: '',
    address: '',
    ownerEmail: '',
    region: 'ca-central-1',
    additionalMembers: '',
  });

  const derivedName = deriveDisplayName(form.homeId);

  const update = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      // Comma- or whitespace-separated extra members. Each gets a Memberships row + invite email.
      const additionalMembers = form.additionalMembers
        .split(/[,\s]+/)
        .map((s) => s.trim().toLowerCase())
        .filter((s) => s.length > 0 && s.includes('@'));
      const result = await api.createHome({
        homeId: form.homeId,
        displayName: derivedName,
        address: form.address,
        ownerEmail: form.ownerEmail,
        region: form.region,
        additionalMembers,
      });
      // If the address matched a previously soft-deleted home, the backend returned the
      // OLD homeId — navigate there (the existing AWS account gets reused).
      if (result.reused && result.reusedFromHomeId) {
        alert(`Reused soft-deleted home "${result.reusedFromHomeId}" (matched on address). Existing AWS account will be redeployed instead of creating a new one.`);
      }
      navigate(`/homes/${result.homeId}`, { replace: true });
    } catch (e: any) {
      setError(e.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-xl">
      <h1 className="text-2xl font-semibold text-brand-ink mb-6">New Home</h1>
      <p className="text-slate-600 text-sm mb-6">
        Provisioning creates a new AWS account, deploys the test infrastructure, replicates
        the Apple Sign-In secret, and sends an invite to the owner. Takes ~10–20 minutes.
      </p>

      <form onSubmit={onSubmit} className="space-y-5 bg-white border border-slate-200 rounded-lg p-6">
        <Field
          label="Home ID"
          hint="camelCase, 3–32 chars (e.g. 10SelbyDr)"
        >
          <input
            required
            pattern="^[a-zA-Z0-9]{3,32}$"
            value={form.homeId}
            onChange={update('homeId')}
            className={fieldClass}
            placeholder="10SelbyDr"
          />
          {derivedName && (
            <div className="mt-1.5 text-xs text-slate-600">
              Display name: <span className="text-slate-500">{derivedName}</span>
            </div>
          )}
        </Field>
        <Field label="Address" hint="Start typing — Google autofills">
          <AddressAutocomplete
            value={form.address}
            onChange={(v) => setForm({ ...form, address: v })}
            className={fieldClass}
            placeholder="720 Front Rd, Pickering, Ontario"
          />
        </Field>
        <Field label="Owner email" hint="Apple-account email of the home's primary owner">
          <input type="email" required value={form.ownerEmail} onChange={update('ownerEmail')} className={fieldClass} />
        </Field>
        <Field
          label="Additional members"
          hint="Optional, comma- or space-separated emails — each gets an invite"
        >
          <input
            value={form.additionalMembers}
            onChange={update('additionalMembers')}
            className={fieldClass}
            placeholder="alice@example.com, bob@example.com"
          />
        </Field>
        <Field label="Region">
          <select value={form.region} onChange={update('region')} className={fieldClass}>
            <option value="ca-central-1">ca-central-1 (Canada Central)</option>
            <option value="us-east-1">us-east-1 (N. Virginia)</option>
          </select>
        </Field>

        {error && (
          <div className="p-3 rounded bg-red-50 border border-red-200 text-red-700 text-sm">
            {error}
          </div>
        )}

        <div className="flex gap-3 pt-2">
          <button
            type="submit"
            disabled={submitting}
            className="px-4 py-2 rounded bg-brand-ink hover:bg-ink-800 text-brand-bone font-medium disabled:opacity-50"
          >
            {submitting ? 'Provisioning…' : 'Provision Home'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/')}
            className="px-4 py-2 rounded border border-slate-300 text-ink-800 hover:text-brand-ink hover:border-slate-400"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

const fieldClass = 'w-full bg-white border border-slate-300 rounded px-3 py-2 text-sm text-brand-ink placeholder:text-slate-400 focus:border-slate-400 focus:outline-none';

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="flex items-baseline justify-between mb-1.5">
        <span className="text-sm font-medium text-brand-ink">{label}</span>
        {hint && <span className="text-xs text-slate-600">{hint}</span>}
      </div>
      {children}
    </label>
  );
}
